/**
 * Colours that tie an instance's chip to its row in the manage panel.
 *
 * Several sets can have instances interleaved in one table, and the only thing
 * telling the reader which chip belongs to which set is the colour. So the colour
 * has to be a property of the set, stable across both surfaces and across
 * reloads -- picked from the set's own id, never from its position in a list,
 * which changes as sets are added.
 */
export interface TaskSetColour {
	/** Chip fill, and the swatch beside the set's name in the panel. */
	readonly background: string;
	/** Chip text and icon, and the panel's View action for that set. */
	readonly foreground: string;
}

/**
 * Foreground carries the text, so each pair is chosen for contrast on its own
 * background rather than for variety. Ordered so the first two sets on a project
 * are the most distinguishable pair.
 *
 * Raw hex, deliberately outside the theme. These are identity markers, not
 * semantic state: the design system's pill variants describe what something IS
 * (success, information), and a colour that only means "a different set from the
 * one above" has no equivalent there. Widening the shared component to take an
 * arbitrary colour would cost every other consumer the meaning its variants
 * carry, so this palette stays local.
 *
 * The condition for changing that: a second consumer wanting this same shape --
 * a clickable, identity-coloured chip. That is the point at which it earns a
 * place in the design system, and at which these values become tokens rather
 * than literals.
 */
const PALETTE: readonly TaskSetColour[] = [
	{background: '#ede4fd', foreground: '#5a18b6'},
	{background: '#d8efe9', foreground: '#0f766e'},
	{background: '#fde8d7', foreground: '#a04a00'},
	{background: '#dce7fb', foreground: '#1d4ed8'},
	{background: '#fbe1ec', foreground: '#a3155f'},
	{background: '#e5e9c9', foreground: '#4d6b0f'},
];

/**
 * A stable colour for a set.
 *
 * Hashed from the id rather than indexed by list position: position changes when
 * a set is created or removed, which would silently recolour every chip on the
 * page and break the association the colour exists to carry.
 *
 * Two sets can still collide on a project with more sets than the palette has
 * entries. That is a legibility cost, not a correctness one, and it beats
 * generating colours whose contrast nothing has checked.
 */
export const taskSetColour = (taskSetId: string | null | undefined): TaskSetColour => {
	if (!taskSetId) {
		return PALETTE[0];
	}
	let hash = 0;
	for (let i = 0; i < taskSetId.length; i++) {
		// Same shape as the common string hash, kept in 32-bit range so a long
		// Relay global id cannot drift into float territory and lose its low bits.
		hash = (hash * 31 + taskSetId.charCodeAt(i)) | 0;
	}
	return PALETTE[Math.abs(hash) % PALETTE.length];
};
